/**
 * Packages and the prospect page (brief section 7.6): one private page per
 * prospect with the videos, PDFs and ROI the seller picked.
 *
 * Access: an email is always asked, never a password. A company address
 * opens the page with any email of the same domain (colleagues can open a
 * forwarded link); a personal address (Gmail, Outlook...) only with that
 * exact email.
 */

/** Personal mailboxes: only the exact address opens the page. */
const PERSONAL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'hotmail.fr',
  'live.com',
  'live.ca',
  'live.fr',
  'msn.com',
  'yahoo.com',
  'yahoo.ca',
  'yahoo.fr',
  'ymail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'gmx.com',
  'gmx.de',
  'gmx.net',
  'web.de',
  'orange.fr',
  'free.fr',
  'sfr.fr',
  'videotron.ca',
  'bell.net',
  'sympatico.ca',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'mail.com',
  'zoho.com',
  'yandex.com',
  't-online.de',
  'libero.it',
]);

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normEmail = (email: unknown) =>
  String(email ?? '')
    .trim()
    .toLowerCase();

export function isPersonalDomain(domain: string): boolean {
  return PERSONAL_DOMAINS.has(domain.toLowerCase());
}

export type PackageAccess = { domain: string; email: null } | { domain: null; email: string };

/** The access rule given by the recipient's email; null when it is not an email. */
export function accessFor(recipientEmail: unknown): PackageAccess | null {
  const email = normEmail(recipientEmail);
  if (!EMAIL.test(email)) return null;
  const domain = email.split('@')[1]!;
  return isPersonalDomain(domain) ? { domain: null, email } : { domain, email: null };
}

/** Does this email open the page? */
export function emailOpens(access: PackageAccess, visitor: unknown): boolean {
  const email = normEmail(visitor);
  if (!EMAIL.test(email)) return false;
  if (access.email) return email === access.email;
  return email.split('@')[1] === access.domain;
}

export type PackageState = 'active' | 'expires_soon' | 'expired';

/** Expired after its expiry day; "Expires soon" in its last 7 days (prototype). */
export function packageState(expiresOn: unknown, today: string): PackageState {
  const day = typeof expiresOn === 'string' ? expiresOn.slice(0, 10) : '';
  if (!day) return 'active';
  if (day < today) return 'expired';
  const soon = new Date(`${today}T12:00:00Z`);
  soon.setUTCDate(soon.getUTCDate() + 7);
  return day <= soon.toISOString().slice(0, 10) ? 'expires_soon' : 'active';
}

export const PACKAGE_STATE_LABELS: Record<PackageState, string> = {
  active: 'Active',
  expires_soon: 'Expires soon',
  expired: 'Expired',
};

/** The next steps a prospect can ask for; each becomes a HubSpot task. */
export const PACKAGE_REQUESTS = ['call', 'question', 'quote', 'visit'] as const;
export type PackageRequest = (typeof PACKAGE_REQUESTS)[number];

export type PageLanguage = 'en' | 'fr';

/** The prospect page's words, in English or French (the package's language). */
export const PROSPECT_TEXTS = {
  en: {
    privatePage: 'Private page',
    preparedFor: 'Prepared for',
    availableUntil: 'Available until',
    workEmail: 'Your work email',
    open: 'Open the page',
    badEmail: 'Enter a valid email.',
    refused:
      'This email cannot open this page. Use your work email, or ask your Waste Robotics representative to share it with you.',
    notIndexed: 'This page is not indexed by search engines.',
    expired: 'This link has expired.',
    expiredHint: 'Contact your Waste Robotics representative for a new link.',
    missing: 'This link is not valid.',
    roi: 'Your return on investment',
    payback: 'Payback (years)',
    roiYear1: 'Year 1 ROI %',
    roiOver: (n: number) => `ROI over ${n} years %`,
    annualProfit: 'Annual gross profit',
    noPayback: 'No payback',
    downloadRoi: 'Download the ROI (PDF)',
    videos: 'Videos',
    documents: 'Documents',
    watch: 'Watch',
    view: 'View',
    download: 'Download',
    nextSteps: 'Next steps',
    requests: {
      call: ['Book a call', 'Pick a time that works for you.'],
      question: ['Ask a question', 'We answer within one business day.'],
      quote: ['Request a budget price', 'Share a few details about your line.'],
      visit: ['Visit a reference site', 'See a system running at another operator.'],
    } as Record<PackageRequest, [string, string]>,
    requestPlaceholder: 'Your message (optional)',
    send: 'Send',
    sent: 'Thank you. Your Waste Robotics representative will get back to you.',
    yourContact: 'Your contact at Waste Robotics',
    saveContact: 'Save contact',
    privacy:
      'Waste Robotics records when this page is opened and what is viewed, to follow up with you.',
    privacyLink: 'Privacy policy',
    close: 'Close',
  },
  fr: {
    privatePage: 'Page privée',
    preparedFor: 'Préparée pour',
    availableUntil: 'Disponible jusqu’au',
    workEmail: 'Votre courriel professionnel',
    open: 'Ouvrir la page',
    badEmail: 'Entrez un courriel valide.',
    refused:
      'Ce courriel ne peut pas ouvrir cette page. Utilisez votre courriel professionnel, ou demandez à votre représentant Waste Robotics de vous la partager.',
    notIndexed: 'Cette page n’est pas indexée par les moteurs de recherche.',
    expired: 'Ce lien a expiré.',
    expiredHint: 'Communiquez avec votre représentant Waste Robotics pour obtenir un nouveau lien.',
    missing: 'Ce lien n’est pas valide.',
    roi: 'Votre retour sur investissement',
    payback: 'Retour (années)',
    roiYear1: 'RSI année 1 %',
    roiOver: (n: number) => `RSI sur ${n} ans %`,
    annualProfit: 'Profit brut annuel',
    noPayback: 'Pas de retour',
    downloadRoi: 'Télécharger le RSI (PDF)',
    videos: 'Vidéos',
    documents: 'Documents',
    watch: 'Regarder',
    view: 'Voir',
    download: 'Télécharger',
    nextSteps: 'Prochaines étapes',
    requests: {
      call: ['Planifier un appel', 'Choisissez un moment qui vous convient.'],
      question: ['Poser une question', 'Nous répondons en un jour ouvrable.'],
      quote: ['Demander un prix budgétaire', 'Donnez-nous quelques détails sur votre ligne.'],
      visit: [
        'Visiter un site de référence',
        'Voyez un système en marche chez un autre exploitant.',
      ],
    } as Record<PackageRequest, [string, string]>,
    requestPlaceholder: 'Votre message (facultatif)',
    send: 'Envoyer',
    sent: 'Merci. Votre représentant Waste Robotics vous répondra.',
    yourContact: 'Votre contact chez Waste Robotics',
    saveContact: 'Enregistrer le contact',
    privacy:
      'Waste Robotics enregistre l’ouverture de cette page et ce qui est consulté, pour assurer le suivi avec vous.',
    privacyLink: 'Politique de confidentialité',
    close: 'Fermer',
  },
} as const;

/** A seller's card as a vCard 3.0 (Save contact, and the QR code). */
export function vCard(card: {
  firstName: string;
  lastName: string;
  title?: string | null;
  email: string;
  mobile?: string | null;
  office?: string | null;
  linkedin?: string | null;
  /** "Met at IFAT 2026": where the visitor met the seller (My business card). */
  note?: string | null;
}): string {
  const esc = (v: string) => v.replace(/([\\;,])/g, '\\$1').replace(/\r?\n/g, ' ');
  return [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${esc(card.lastName)};${esc(card.firstName)};;;`,
    `FN:${esc(`${card.firstName} ${card.lastName}`.trim())}`,
    'ORG:Waste Robotics',
    card.title ? `TITLE:${esc(card.title)}` : null,
    `EMAIL;TYPE=WORK:${esc(card.email)}`,
    card.mobile ? `TEL;TYPE=CELL:${esc(card.mobile)}` : null,
    card.office ? `TEL;TYPE=WORK:${esc(card.office)}` : null,
    card.linkedin ? `URL:${esc(card.linkedin)}` : null,
    card.note ? `NOTE:${esc(card.note)}` : null,
    'END:VCARD',
  ]
    .filter(Boolean)
    .join('\r\n');
}

/** Waste Robotics as the prospect page and the PDFs sign (prototype). */
export const WR_COMPANY = {
  name: 'Waste Robotics',
  address: '11410 Louis-Loranger Blvd., Trois-Rivières, QC G9B 0T8',
  email: 'info@wasterobotics.com',
  phone: '819 201-2525',
  tagline: 'Robotic waste sorting since 2016',
} as const;
